import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  IconButton,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { useUpdatePropertyMutation, useDeletePropertyMutation } from '../store/docApi';
import { iconOptions, renderIconSvg } from '../components/charts/iconsSvg';
import ColorwayLegend from './ColorwayLegend';
import ChromaBrowser from './ChromaBrowser';

export default function ColorwayDetails({ item, subId }) {
  const [colorway, setColorway] = useState(null);
  const [updateProperty] = useUpdatePropertyMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewChromaDialogOpen, setIsNewChromaDialogOpen] = useState(false);
  const [newChroma, setNewChroma] = useState({
    id: '',
    color: '#000000',
    icon: '',
    isSolid: false,
    name: '',
    code: '',
    amount: '',
    note: '',
    isPattern: false
  });
  const [browsingChromaIndex, setBrowsingChromaIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingChroma, setIsEditingChroma] = useState(false);
  const [editingChromaIndex, setEditingChromaIndex] = useState(null);

  useEffect(() => {
    if (item && item.colorways && subId && item.colorways[subId]) {
      setColorway({ id: subId, ...item.colorways[subId] });
    }
  }, [item, subId]);

  const handleSelectChroma = (chroma) => {
    if (browsingChromaIndex !== null) {
      const updatedChromas = colorway.chromas.map((c, i) =>
        i === browsingChromaIndex ? { ...c, color: chroma.color, code: chroma.code, name: chroma.name } : c
      );
      setColorway({ ...colorway, chromas: updatedChromas });
      setBrowsingChromaIndex(null);
    } else {
      setNewChroma({
        ...newChroma,
        color: chroma.color,
        code: chroma.code,
        name: chroma.name
      });
      setIsNewChromaDialogOpen(true);
    }
    setIsDialogOpen(false);
  };

  const handleEditChroma = (index, updatedChroma) => {
    const updatedChromas = colorway.chromas.map((c, i) => (i === index ? { ...c, ...updatedChroma } : c));
    setColorway({ ...colorway, chromas: updatedChromas });
  };

  const handleAddOrUpdateChroma = () => {
    if (isEditingChroma) {
      // Update existing chroma
      const updatedChromas = colorway.chromas.map((c, i) => (i === editingChromaIndex ? newChroma : c));
      setColorway({ ...colorway, chromas: updatedChromas });
    } else {
      // Add new chroma
      setColorway({ ...colorway, chromas: [...colorway.chromas, newChroma] });
    }
    // Reset state
    setNewChroma({
      id: '',
      color: '#000000',
      icon: '',
      name: '',
      code: '',
      amount: '',
      note: ''
    });
    setIsEditingChroma(false);
    setEditingChromaIndex(null);
    setIsNewChromaDialogOpen(false);
  };

  const handleEditChromaClick = (index) => {
    setNewChroma(colorway.chromas[index]);
    setIsEditingChroma(true);
    setEditingChromaIndex(index);
    setIsNewChromaDialogOpen(true);
  };

  const handleCancelChromaEdit = () => {
    setNewChroma({
      id: '',
      color: '#000000',
      icon: '',
      name: '',
      code: '',
      amount: '',
      note: ''
    });
    setIsEditingChroma(false);
    setEditingChromaIndex(null);
    setIsNewChromaDialogOpen(false);
  };

  const handleSave = () => {
    onSaveProperty('name', colorway.name);
    onSaveProperty('chromas', colorway.chromas);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (item && item.colorways && subId && item.colorways[subId]) {
      setColorway({ id: subId, ...item.colorways[subId] });
    }
    setIsEditing(false);
  };

  function deleteColorway() {
    setDeleteConfirmOpen(false);
    deleteProperty({ pk: item.pk, id: item.id, name: `colorways/${subId}` });
    navigate(`/workspaces/${item.pk}/${item.id}`);
  }

  function onSaveProperty(name, value) {
    updateProperty({ label: item.label, pk: item.pk, id: item.id, name: `colorways/${subId}/${name}`, value });
  }

  return (
    colorway && (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {colorway.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {colorway.id}
            </Typography>
          </Box>
          {!isEditing && (
            <ButtonGroup variant="outlined" size="small">
              <Tooltip title="Edit Colorway">
                <Button startIcon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Delete Colorway">
                <Button startIcon={<DeleteOutlined />} color="error" onClick={() => setDeleteConfirmOpen(true)}>
                  Delete
                </Button>
              </Tooltip>
            </ButtonGroup>
          )}
        </Box>
        {isEditing ? (
          <Box sx={{ mt: 3, mb: 3, backgroundColor: 'white', p: 2 }}>
            <TextField
              value={colorway.name || ''}
              onChange={(e) => setColorway({ ...colorway, name: e.target.value })}
              placeholder="Enter colorway name"
              label="Colorway Name"
              fullWidth
              sx={{ mb: 2 }}
            />
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: '0.875rem'
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Pattern?</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Color</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Icon</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Solid?</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Amount</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Note</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {colorway.chromas &&
                  colorway.chromas.map((chroma, index) => (
                    <tr key={index} style={{ borderTop: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField
                          value={chroma.id || ''}
                          onChange={(e) => handleEditChroma(index, { id: e.target.value })}
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <Checkbox
                          checked={chroma.isPattern || false}
                          onChange={(e) => handleEditChroma(index, { isPattern: e.target.checked })}
                          size="small"
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            component="label"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: chroma.color,
                              border: '1px solid #ccc',
                              borderRadius: 1,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: 'primary.main'
                              }
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 10 10">
                              {renderIconSvg(chroma.icon, chroma.color, chroma.isSolid)}
                            </svg>
                            <input
                              type="color"
                              value={chroma.color}
                              onChange={(e) => handleEditChroma(index, { color: e.target.value })}
                              style={{
                                position: 'absolute',
                                width: 0,
                                height: 0,
                                opacity: 0
                              }}
                            />
                          </Box>
                        </Box>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <Select
                          value={chroma.icon || ''}
                          onChange={(e) => handleEditChroma(index, { icon: e.target.value })}
                          size="small"
                          sx={{ height: '32px' }}
                        >
                          {iconOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <Checkbox
                          checked={chroma.isSolid || false}
                          onChange={(e) => handleEditChroma(index, { isSolid: e.target.checked })}
                          size="small"
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField
                          value={chroma.code || ''}
                          onChange={(e) => handleEditChroma(index, { code: e.target.value })}
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField
                          value={chroma.name || ''}
                          onChange={(e) => handleEditChroma(index, { name: e.target.value })}
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField
                          value={chroma.amount || ''}
                          onChange={(e) => handleEditChroma(index, { amount: e.target.value })}
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField
                          value={chroma.note || ''}
                          onChange={(e) => handleEditChroma(index, { note: e.target.value })}
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <IconButton onClick={() => handleEditChromaClick(index)} size="small" sx={{ padding: '4px' }}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            setColorway({
                              ...colorway,
                              chromas: colorway.chromas.filter((_, i) => i !== index)
                            })
                          }
                          size="small"
                          sx={{ padding: '4px' }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setIsNewChromaDialogOpen(true)}>
                Add Chroma
              </Button>
              <ButtonGroup variant="outlined" size="small">
                <Button variant="contained" onClick={handleSave} startIcon={<EditOutlined />}>
                  Save
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </ButtonGroup>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 3, mb: 3,  p: 2 }}>
            <ColorwayLegend colorway={colorway} />
          </Box>
        )}

        {/* Dialog for Chroma Browser */}
        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth>
          <DialogTitle>Browse Chromas</DialogTitle>
          <DialogContent>
            <ChromaBrowser onSelect={handleSelectChroma} onClose={() => setIsDialogOpen(false)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Adding/Editing Chroma */}
        <Dialog open={isNewChromaDialogOpen} onClose={handleCancelChromaEdit} fullWidth>
          <DialogTitle>{isEditingChroma ? 'Edit Chroma' : 'Add New Chroma'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="ID"
                value={newChroma.id}
                onChange={(e) => setNewChroma({ ...newChroma, id: e.target.value })}
                size="small"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Color"
                  type="color"
                  value={newChroma.color}
                  onChange={(e) => setNewChroma({ ...newChroma, color: e.target.value })}
                  size="small"
                  sx={{ width: '200px' }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsDialogOpen(true);
                    setIsNewChromaDialogOpen(false);
                  }}
                >
                  Browse Colors
                </Button>
              </Box>
              <Select value={newChroma.icon} onChange={(e) => setNewChroma({ ...newChroma, icon: e.target.value })} size="small">
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Checkbox checked={newChroma.isSolid} onChange={(e) => setNewChroma({ ...newChroma, isSolid: e.target.checked })} />
              <TextField
                label="Code"
                value={newChroma.code}
                onChange={(e) => setNewChroma({ ...newChroma, code: e.target.value })}
                size="small"
              />
              <TextField
                label="Name"
                value={newChroma.name}
                onChange={(e) => setNewChroma({ ...newChroma, name: e.target.value })}
                size="small"
              />
              <TextField
                label="Note"
                value={newChroma.note}
                onChange={(e) => setNewChroma({ ...newChroma, note: e.target.value })}
                size="small"
              />
              <TextField
                label="Amount"
                type="number"
                value={newChroma.amount}
                onChange={(e) => setNewChroma({ ...newChroma, amount: e.target.value })}
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelChromaEdit}>Cancel</Button>
            <Button onClick={handleAddOrUpdateChroma} variant="contained">
              {isEditingChroma ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            <Typography>Delete this colorway from {item.name}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={deleteColorway} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  );
}
